// AssistantSection — the chat Section: threads (list/create/rename) + messages (list/append),
// calling the server-side Claude API with the system prompt, tools and db_id link rendering
// (BUILD_BRIEF_assistant_panel.md stage 2 — stage 1 shipped tool-less on purpose so a chat that
// already worked in isolation would isolate every stage-2 bug to the tools). Threads are
// flat (id, title, timestamps) — a SelectCell dropdown + "+ New" button, per decision 8.
// Reload-survival comes free: the newest thread is the default selection, and every message is
// read back from data/assistant.db, never held only in React state.
//
// db_id links (decision 6, superseded by the /go/<db_id> route built in 505ad3f): the system
// prompt tells the model to cite rows as `prod:<id>` / `line:<id>` / `fam:<id>` / `proj:<id>`.
// renderContent() below turns those tokens into plain anchors to /go/<db_id> — the route already
// resolves kind:id to the right screen and 302s there, so this is deliberately NOT a second nav
// resolver: no nav-link.js import, no client-side tree-coordinate logic.
//
// Deliberately dumb about the domain otherwise — it does not know catalog/product/project shapes
// beyond that one citation format. Replaces the reserved, disabled placeholder Alex asked for on
// 2026-08-01 ("even if it's not working, to make sure I don't forget").
//
// The window itself (messages, composer, model picker) is ChatView, shared with the :8765
// Onshape panel so both chats look the same (2026-10-11). This file keeps what is :8002's
// own: threads, specialists, and where a message goes.
import { useEffect, useRef, useState } from 'react';
import SelectCell from './SelectCell.jsx';
import ChatView from './ChatView.jsx';
import { BTN_PRIMARY_OUTLINE } from '../theme-tokens.js';

async function readJson(res) {
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

// Turns db_id tokens (`prod:123`, `line:5`, `fam:35`, `proj:2`) in an answer into markdown
// links to /go/<token>. The /go/ route (src/db-id-link.js, server-web.js) does all the nav
// resolution server-side; this is just string-rewriting. Inline code is left alone.
const DB_ID_TOKEN_RE = /(`[^`]*`)|\b((?:prod|line|fam|proj):\d+)\b/g;
function linkCitations(content) {
  return String(content).replace(DB_ID_TOKEN_RE, (all, code, token) => (code ? all : `[${token}](/go/${token})`));
}

// screen: what the page has open, e.g. { kind: 'product', id, label } on the Products
// screen. It goes with every message and is the only thing the chat may write to. After a
// turn that could have written, an `inoviu:assistant-turn` window event tells the page to
// re-read (ProductsView), including after Stop, since a write may land before the stop.
export default function AssistantSection({ screen = null }) {
  const [threads, setThreads] = useState([]);
  const [threadId, setThreadId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [models, setModels] = useState({ models: [], default: null });
  // The request in flight, so Stop can cancel it; and a thread send() has just created,
  // whose (empty) message list must not be read over the message being sent.
  const inFlight = useRef(null);
  const createdBySend = useRef(null);

  useEffect(() => {
    fetch('/api/assistant/models').then(readJson).then(setModels).catch(() => {});
  }, []);

  // The specialist roster comes from the SERVER, which derives it from SPECIALTY_*.md on
  // disk — so the slash commands the panel offers and the briefs the model is given can
  // never disagree. Alex, 2026-08-13: "/Electrical, /Piping to get same as specialist".
  useEffect(() => {
    fetch('/api/assistant/subjects').then(readJson).then(setSubjects).catch(() => setSubjects([]));
  }, []);

  // Load the thread list once, then default to the newest — this is the whole reload story:
  // the DB already has the messages, so re-selecting the same (now-newest) thread on mount
  // is all "survives a reload" requires.
  useEffect(() => {
    fetch('/api/assistant/threads')
      .then(readJson)
      .then((rows) => {
        setThreads(rows);
        if (rows.length) setThreadId(rows[0].id);
      })
      .catch((e) => setError(e.message));
  }, []);

  useEffect(() => {
    if (threadId == null) { setMessages([]); return; }
    if (threadId === createdBySend.current) { createdBySend.current = null; return; }
    fetch(`/api/assistant/threads/${threadId}/messages`)
      .then(readJson)
      .then(setMessages)
      .catch((e) => setError(e.message));
  }, [threadId]);

  const newThread = () => {
    setError(null);
    fetch('/api/assistant/threads', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
    })
      .then(readJson)
      .then((t) => {
        setThreads((prev) => [t, ...prev]);
        setThreadId(t.id);
      })
      .catch((e) => setError(e.message));
  };

  // ChatView hands over the text, the picked model and any pasted pictures. A rejected
  // promise puts them back in the box — on a failure, and on Stop.
  const send = (raw, model, images = []) => {
    // `/electrical what is R11E?` opens a NEW thread scoped to that specialist and asks
    // the rest. A slash that is NOT a known specialty falls through and is sent as an
    // ordinary message — a typo must not be silently swallowed, and `/` is legitimate
    // text (paths, sizes like `1/2"`).
    const cmd = /^\/([A-Za-z0-9]+)\s*([\s\S]*)$/.exec(raw);
    const hit = cmd && subjects.find((x) => x.slug === cmd[1].toLowerCase());
    const content = hit ? cmd[2].trim() : raw;
    setError(null);
    if (hit && !content && !images.length) {
      // Bare `/electrical` — open the thread and wait, rather than sending an empty turn.
      return fetch('/api/assistant/threads', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject: hit.slug, title: `${hit.label} — new` }),
      }).then(readJson).then((t) => {
        setThreads((prev) => [t, ...prev]);
        setThreadId(t.id);
      }).catch((e) => { setError(e.message); throw e; });
    }
    let activeId = hit ? null : threadId;
    const controller = new AbortController();
    inFlight.current = controller;
    const { signal } = controller;
    setSending(true);
    // Shown at once, replaced by the saved rows when the answer comes back.
    setMessages((prev) => [...(hit ? [] : prev), { id: 'pending', role: 'user', content, images }]);

    const post = (id) => fetch(`/api/assistant/threads/${id}/messages`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, signal,
      body: JSON.stringify({ content, model, images, screen }),
    })
      .then(readJson)
      .then((r) => {
        // Dedupe by id: opening a new thread re-reads its messages, and that read can land
        // after the user's turn was saved, so the same row would otherwise show twice.
        const fresh = new Set(['pending', r.userMessage.id, r.assistantMessage.id]);
        setMessages((prev) => [...prev.filter((m) => !fresh.has(m.id)), r.userMessage, r.assistantMessage]);
        setThreads((prev) => {
          const rest = prev.filter((t) => t.id !== id);
          return [r.thread, ...rest];
        });
      });

    return (activeId == null
      ? fetch('/api/assistant/threads', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(hit ? { subject: hit.slug } : {}),
          signal,
        }).then(readJson).then((t) => {
          activeId = t.id;
          createdBySend.current = t.id;
          setThreadId(t.id);
          setThreads((prev) => [t, ...prev]);
          return post(activeId);
        })
      : post(activeId)
    )
      .catch((e) => {
        // Stopped or failed, the server saved nothing from this turn (assistant-routes.js),
        // so dropping the pending bubble leaves the view equal to the thread. Stop is not
        // an error: no message, the text simply comes back to the box.
        if (e.name !== 'AbortError') setError(e.message);
        setMessages((prev) => prev.filter((m) => m.id !== 'pending'));
        throw e;
      })
      .finally(() => {
        if (inFlight.current === controller) inFlight.current = null;
        setSending(false);
        if (screen) window.dispatchEvent(new CustomEvent('inoviu:assistant-turn', { detail: screen }));
      });
  };
  const stop = () => inFlight.current?.abort();

  const threadOptions = threads.map((t) => ({
    value: String(t.id),
    label: t.subject ? `${t.subject} · ${t.title}` : t.title,
  }));
  const activeSubject = threads.find((t) => String(t.id) === String(threadId))?.subject || null;

  return (
    // FILLS its slot, never sizes itself. A hardcoded height here (it was 340) survived
    // from stage 1, when this lived inside PlacementSidePanel and had to state a size.
    // The workspace now owns the height and its drag divider, so a fixed height meant the
    // divider moved a container the content ignored. Alex's rule, UI_VISION §3: content
    // does not own its chrome — that includes its size.
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px' }}>
        {threads.length > 0 ? (
          <div style={{ flex: 1, minWidth: 0 }}>
            <SelectCell
              value={threadId == null ? '' : String(threadId)}
              onChange={(v) => setThreadId(v ? Number(v) : null)}
              options={threadOptions}
              title="Switch conversation"
            />
          </div>
        ) : (
          <div style={{ flex: 1, fontSize: 'var(--fs-micro)', color: 'var(--th-text-hint)' }}>
            No conversation yet.
          </div>
        )}
        <button
          onClick={newThread}
          style={{ ...BTN_PRIMARY_OUTLINE, fontSize: 'var(--fs-micro)', padding: '3px 8px', flexShrink: 0 }}
        >
          + New
        </button>
      </div>

      <ChatView
        messages={messages}
        busy={sending}
        error={error}
        onSend={send}
        onStop={stop}
        allowImages
        models={models.models}
        defaultModel={models.default}
        transform={linkCitations}
        // NOT "this function" any more (2026-08-13): the panel moved out of the Function
        // screen's side panel into the workspace's right column, so it now appears on
        // Catalog, Products, 3D Model and Projects alike. A placeholder naming one screen
        // was accurate in exactly one of them and quietly wrong in the other three.
        placeholder={
          activeSubject
            ? `Ask the ${activeSubject} specialist…`
            : (subjects.length
                ? `Ask anything, or /${subjects[0].label} for a specialist…`
                : 'Ask about the catalogue, products or projects…')
        }
      />
    </div>
  );
}
